import React, { createContext, useContext, useState } from 'react';
import { useAnswer } from '../../components/Capture';
import Advice from '../../components/Advice';
import { TrendingUp } from 'lucide-react';

/* ======================================================
   CloseTimeline
   What NobleOak's close speed means for its ASX reporting cycle,
   step by step, compared with a BD4 hard close.

   Today: audit/adjustments due 3 weeks before release, board update
   2 weeks before release.
   BD4 target: the working days a faster close frees up go first to
   audit (up to AUDIT_EXTRA), then are shared between board documents
   and the investor presentation.

   Working back from the results release (R):
     - Board update:                         R − 14 days (two weeks before release)
     - Audit complete, adjustments captured: R − 21 days (one week to finish board documents)
     - Investor deck + release pack finalised in the last two weeks
   Working forward from period end:
     - Soft close: three business days before hard close (BD2 on the target)
     - Hard close: the business day chosen on the slide (BD4 on the target)

   Sources: NobleOak investor key-events calendar and ASX releases
   (1H FY26 results 27 Feb 2026; FY26 results 28 Aug 2026).
====================================================== */

const PERIODS = {
  'FY26 full year': { end: '2026-06-30', release: '2026-08-28' },
  '1H FY26 half year': { end: '2025-12-31', release: '2026-02-27' },
};

// NSW public holidays falling inside these windows
const HOLIDAYS = new Set(['2025-12-25', '2025-12-26', '2026-01-01', '2026-01-26']);

export const CLOSE_DAY = { 'Day 1–5': 5, 'Day 6–10': 10, 'Day 11–15': 15, 'Day 16+': 16 };
const TARGET = { soft: 2, hard: 4 };

const BOARD_BEFORE_RELEASE = 14;
const AUDIT_BEFORE_RELEASE = 21;
const AUDIT_EXTRA = 5;

const d = (iso) => new Date(`${iso}T00:00:00Z`);
const iso = (date) => date.toISOString().slice(0, 10);
const addDays = (date, n) => new Date(date.getTime() + n * 86400000);
const isWorkday = (date) => {
  const day = date.getUTCDay();
  return day !== 0 && day !== 6 && !HOLIDAYS.has(iso(date));
};
const addWorkdays = (date, n) => {
  let cur = date;
  let left = n;
  while (left > 0) {
    cur = addDays(cur, 1);
    if (isWorkday(cur)) left -= 1;
  }
  return cur;
};
// working days after `from`, up to and including `to`
const workdaysBetween = (from, to) => {
  let n = 0;
  for (let cur = addDays(from, 1); cur <= to; cur = addDays(cur, 1)) if (isWorkday(cur)) n += 1;
  return n;
};
const daysBetween = (a, b) => Math.round((b - a) / 86400000);
const fmt = (date) => date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

export const buildCycle = (periodName, soft, hard, releaseOverride) => {
  const p = PERIODS[periodName];
  const end = d(p.end);
  const release = releaseOverride || d(p.release);
  const softD = addWorkdays(end, soft);
  const hardD = addWorkdays(end, hard);
  const auditD = addDays(release, -AUDIT_BEFORE_RELEASE);
  const boardD = addDays(release, -BOARD_BEFORE_RELEASE);
  const steps = [
    { key: 'softClose', name: 'Soft close', from: end, to: softD, days: soft, unit: `BD${soft}` },
    { key: 'hardClose', name: 'Hard close', from: softD, to: hardD, days: hard - soft, unit: `BD${hard}` },
    { key: 'audit', name: 'Audit complete, adjustments captured', from: hardD, to: auditD, days: workdaysBetween(hardD, auditD) },
    { key: 'board', name: 'Board documents', from: auditD, to: boardD, days: workdaysBetween(auditD, boardD) },
    { key: 'ir', name: 'Investor presentation & release', from: boardD, to: release, days: workdaysBetween(boardD, release) },
  ];
  return { end, release, total: daysBetween(end, release), steps, audit: steps[2].days };
};

// BD4 target: reinvest the freed-up days (audit first, then board docs + investor presentation)
export const buildTarget = (periodName, today) => {
  const p = PERIODS[periodName];
  const end = d(p.end);
  const { release } = today;
  const softD = addWorkdays(end, TARGET.soft);
  const hardD = addWorkdays(end, TARGET.hard);
  const [, , tAudit, tBoard] = today.steps;
  const gain = workdaysBetween(hardD, today.steps[1].to);
  const plusAudit = Math.min(AUDIT_EXTRA, gain);
  const rest = gain - plusAudit;
  const plusBoard = Math.ceil(rest / 2);
  const plusIr = rest - plusBoard;
  const auditD = addWorkdays(hardD, tAudit.days + plusAudit);
  const boardD = addWorkdays(auditD, tBoard.days + plusBoard);
  const steps = [
    { key: 'softClose', name: 'Soft close', from: end, to: softD, days: TARGET.soft, unit: `BD${TARGET.soft}` },
    { key: 'hardClose', name: 'Hard close', from: softD, to: hardD, days: TARGET.hard - TARGET.soft, unit: `BD${TARGET.hard}` },
    { key: 'audit', name: 'Audit complete, adjustments captured', from: hardD, to: auditD, days: tAudit.days + plusAudit, plus: plusAudit },
    { key: 'board', name: 'Board documents', from: auditD, to: boardD, days: tBoard.days + plusBoard, plus: plusBoard },
    { key: 'ir', name: 'Investor presentation & release', from: boardD, to: release, days: workdaysBetween(boardD, release), plus: plusIr },
  ];
  return { end, release, total: daysBetween(end, release), steps, audit: steps[2].days, gain, plusAudit, plusBoard, plusIr };
};

/* ---------- Period toggle + draggable release date ---------- */

const PeriodContext = createContext(null);
const PERIOD_LABEL = { 'FY26 full year': 'FY26', '1H FY26 half year': '1H FY26' };

export const ClosePeriodProvider = ({ children }) => {
  const [period, setPeriod] = useState('FY26 full year');
  const [releases, setReleases] = useState({}); // period -> ISO date chosen on the slider
  const release = releases[period] ? d(releases[period]) : d(PERIODS[period].release);
  const setRelease = (date) => setReleases((r) => ({ ...r, [period]: date ? iso(date) : undefined }));
  return (
    <PeriodContext.Provider value={{ period, setPeriod, release, setRelease }}>{children}</PeriodContext.Provider>
  );
};

export const PeriodToggle = () => {
  const { period, setPeriod } = useContext(PeriodContext);
  return (
    <div className="segmented" role="group" aria-label="Reporting period">
      {Object.keys(PERIODS).map((p) => (
        <button type="button" key={p} className={p === period ? 'on' : ''} aria-pressed={p === period} onClick={() => setPeriod(p)}>
          {PERIOD_LABEL[p]}
        </button>
      ))}
    </div>
  );
};

// Working days the release can move to: from the earliest date today's close allows, up to the actual release
const releaseOptions = (periodName, hard) => {
  const p = PERIODS[periodName];
  const actual = d(p.release);
  const hardD = addWorkdays(d(p.end), hard);
  const out = [];
  for (let cur = addDays(hardD, AUDIT_BEFORE_RELEASE + 1); cur <= actual; cur = addDays(cur, 1)) {
    // keep at least one working day for audit
    if (isWorkday(cur) && workdaysBetween(hardD, addDays(cur, -AUDIT_BEFORE_RELEASE)) >= 1) out.push(cur);
  }
  return out;
};

export const ReleaseSlider = () => {
  const { period, release, setRelease } = useContext(PeriodContext);
  const { selected } = useAnswer('closeDay');
  const hard = CLOSE_DAY[selected[0]] ?? 10;
  const options = releaseOptions(period, hard);
  if (!options.length) return null;
  const actual = d(PERIODS[period].release);
  const idx = Math.max(0, options.findIndex((o) => iso(o) === iso(release)));
  const current = options[idx] || actual;
  const earlierBy = workdaysBetween(current, actual);
  return (
    <div className="relSlider">
      <label htmlFor="release-slider">
        NobleOak results <b>{fmt(current)}</b>
        {earlierBy > 0 && <span className="plus"> −{earlierBy} BD</span>}
      </label>
      <input
        id="release-slider"
        type="range"
        min={0}
        max={options.length - 1}
        value={options.findIndex((o) => iso(o) === iso(current))}
        onChange={(e) => {
          const next = options[Number(e.target.value)];
          setRelease(iso(next) === iso(actual) ? null : next);
        }}
        aria-label="Drag to report earlier"
      />
      {earlierBy > 0 && (
        <button type="button" className="changeBtn" onClick={() => setRelease(null)}>Reset</button>
      )}
    </div>
  );
};

export const CloseActions = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
    <ReleaseSlider />
    <PeriodToggle />
  </div>
);

/* ---------- One scenario: bar + step row ---------- */

const Scenario = ({ title, cycle, tag }) => {
  const pct = (a, b) => `${(daysBetween(a, b) / cycle.total) * 100}%`;
  return (
    <div className="tl">
      <div className="tlHead">
        <b>{title}</b>
        <span className={`tlTag ${tag.tone}`}>{tag.text}</span>
      </div>
      <div className="tlBar">
        {cycle.steps.map((s) => (
          <div key={s.key} className={`tlSeg ${s.key}`} style={{ width: pct(s.from, s.to) }}>
            {daysBetween(s.from, s.to) > 6 && !['softClose', 'hardClose'].includes(s.key) ? `${s.days} BD` : ''}
          </div>
        ))}
      </div>
      <div className="tlSteps">
        {cycle.steps.map((s) => (
          <div key={s.key} className="tlStep">
            <span className={`dot ${s.key}`} />
            <div>
              <div className="tlStepName">{s.name}</div>
              <div className="tlStepMeta">
                <b>{s.unit || `${s.days} BD`}</b>
                {s.plus > 0 && <span className="plus"> +{s.plus}</span>} · {fmt(s.to)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CloseTimeline = ({ choice }) => {
  const { period, release } = useContext(PeriodContext);
  const hard = CLOSE_DAY[choice] ?? 10;
  const soft = Math.max(1, hard - 3);
  const actual = d(PERIODS[period].release);
  const allowed = releaseOptions(period, hard).map(iso);
  const releaseD = allowed.includes(iso(release)) ? release : actual; // close day changed: fall back to actual
  const earlierBy = workdaysBetween(releaseD, actual);
  const today = buildCycle(period, soft, hard, releaseD);
  const target = buildTarget(period, today);
  const fixed = today.steps[3].days + today.steps[4].days;

  return (
    <>
      <div style={{ display: 'grid', gap: 22 }}>
        <Scenario
          title={`NobleOak today — hard close at ${choice === 'Day 16+' ? 'BD16 or later' : `BD${hard}`}`}
          cycle={today}
          tag={{ text: `${today.audit} BD audit · ${today.steps[3].days} BD board · ${today.steps[4].days} BD investor`, tone: 'warn' }}
        />
        <Scenario
          title="NobleOak with a BD4 hard close"
          cycle={target}
          tag={{
            text: target.gain > 0 ? `${target.gain} working days freed up and reinvested` : 'Already close to BD4',
            tone: 'good',
          }}
        />
      </div>
      <div className="callout" style={{ display: 'flex', gap: 18, alignItems: 'flex-start', fontSize: 22, padding: '20px 28px' }}>
        <TrendingUp size={28} style={{ color: '#C4B5FD', flex: 'none', marginTop: 2 }} />
        <span>
          {earlierBy > 0 ? `Reporting ${earlierBy} BD earlier squeezes audit. ` : ''}
          Board documents and the investor deck need {fixed} working days, so a slow close comes out of audit.{' '}
          {target.gain > 0
            ? `BD4 frees ${target.gain}: ${[
                `+${target.plusAudit} audit`,
                target.plusBoard ? `+${target.plusBoard} board documents` : null,
                target.plusIr ? `+${target.plusIr} investor deck` : null,
              ]
                .filter(Boolean)
                .join(', ')}.`
            : 'At BD4 the window is protected.'}
        </span>
      </div>
      <Advice>
        Reach BD4 in two steps: <b>a BD2 soft close on fixed cut-offs</b>, then <b>daily reconciliations</b> so hard close
        is a sign-off, not a scramble.
      </Advice>
    </>
  );
};

export default CloseTimeline;
